import React from "react";
import PropTypes from "prop-types";
import RSModal from "reactstrap/lib/Modal";
import ModalBody from "reactstrap/lib/ModalBody";
import ModalFooter from "reactstrap/lib/ModalFooter";
import Row from "reactstrap/lib/Row";
import Col from "reactstrap/lib/Col";

import {
  gatherClasses,
  gatherConditionalProps,
  isFunction,
  isString
} from "../../lib/utils";
import Tooltip from "../tooltip/tooltip";
import { handleIERenderingIssues } from "../../lib/modalUtils";
import { Help } from "../../lib/icons";
import Icon from "../icon/icon";
import "./modal.scss";

class Modal extends React.Component {
  constructor(props) {
    super(props);
    this.ref = React.createRef();
  }

  render() {
    const { size } = this.props;
    return size === "xxl" ? this.renderFullPageModal() : this.renderModal();
  }

  onModalOpen = () => {
    const { onOpened } = this.props;

    handleIERenderingIssues(this.ref.current);

    if (isFunction(onOpened)) {
      onOpened();
    }
  };

  renderModal = () => {
    const {
      header,
      dataTestId,
      stickToHeader,
      size,
      backdrop,
      closeOnEscape,
      titleAction,
      docUrl,
      docUrlTooltipText,
      toggle,
      body,
      bodyClassName,
      footer
    } = this.props;

    return (
      <RSModal
        data-testid={dataTestId}
        innerRef={this.ref}
        centered={!stickToHeader}
        fade
        className="oui-modal-dialog-scrollable"
        wrapClassName={gatherClasses(this.props, `wrap-${size}`)}
        modalClassName={gatherClasses(
          null,
          stickToHeader ? "oui-stick-to-header" : ""
        )}
        backdrop={backdrop}
        keyboard={closeOnEscape}
        {...gatherConditionalProps(
          this.props,
          "isOpen",
          "size",
          "autoFocus",
          "toggle",
          "onClosed"
        )}
        onOpened={this.onModalOpen}
      >
        {header && (
          <div className="oui-modal-header">
            <div className="oui-header-left">
              <div className="oui-header-title">{this.getHeader()}</div>
              <div className="oui-header-actions">
                {isFunction(titleAction) && titleAction()}
              </div>
            </div>
            <div className="oui-header-right">
              {docUrl && (
                <Tooltip
                  text={docUrlTooltipText || ""}
                  showToolTip={!!isString(docUrlTooltipText)}
                  placement="bottom"
                >
                  <a
                    href={docUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="oui-doc-url"
                  >
                    <Icon src={Help} clickable />
                  </a>
                </Tooltip>
              )}
              <button
                type="button"
                className="oui-close oui-ml-auto"
                data-dismiss="modal"
                aria-label="Close"
                onClick={toggle}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          </div>
        )}

        {isFunction(body) && (
          <ModalBody className={gatherClasses(null, bodyClassName)}>
            {body()}
          </ModalBody>
        )}

        {isFunction(footer) && <ModalFooter>{footer()}</ModalFooter>}
      </RSModal>
    );
  };

  getHeader() {
    const { header } = this.props;
    if (isString(header)) {
      return <Modal.Header header={header} />;
    }

    if (isFunction(header)) {
      return header();
    }

    return null;
  }

  renderFullPageModal = () => {
    const {
      dataTestId,
      backdrop,
      closeOnEscape,
      header,
      titleAction,
      docUrl,
      docUrlTooltipText,
      toggle,
      body,
      bodyClassName,
      footer
    } = this.props;

    return (
      <RSModal
        data-testid={dataTestId}
        innerRef={this.ref}
        fade={false}
        centered={false}
        wrapClassName={gatherClasses(this.props, "wrap-xxl")}
        contentClassName="modal-content-xxl"
        backdrop={backdrop}
        keyboard={closeOnEscape}
        {...gatherConditionalProps(
          this.props,
          "isOpen",
          "size",
          "autoFocus",
          "toggle",
          "onClosed"
        )}
        onOpened={this.onModalOpen}
      >
        {header && (
          <Row className="oui-header-container">
            <Col sm={{ size: 8 }} className="oui-mx-auto">
              <div className="oui-header">
                <div className="oui-header-left">
                  <div className="oui-header-title">{this.getHeader()}</div>
                  <div className="oui-header-actions">
                    {isFunction(titleAction) && titleAction()}
                  </div>
                </div>
                <div className="oui-header-right">
                  {docUrl && (
                    <Tooltip
                      text={docUrlTooltipText || ""}
                      showToolTip={!!isString(docUrlTooltipText)}
                      placement="bottom"
                    >
                      <a
                        href={docUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="oui-doc-url"
                      >
                        <Icon src={Help} clickable />
                      </a>
                    </Tooltip>
                  )}
                  <button
                    type="button"
                    className="oui-close oui-ml-auto"
                    data-dismiss="modal"
                    aria-label="Close"
                    onClick={toggle}
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
              </div>
            </Col>
          </Row>
        )}

        {isFunction(body) && (
          <Row className="oui-body-container">
            <Col
              sm={{ size: 8 }}
              className="oui-body-inner-container oui-mx-auto"
            >
              <div className={gatherClasses(null, bodyClassName, "oui-body")}>
                {body()}
              </div>
            </Col>
          </Row>
        )}

        {isFunction(footer) && (
          <Row className="oui-footer-container">
            <Col sm={{ size: 8 }} className="oui-mx-auto">
              <div className="oui-footer">{footer()}</div>
            </Col>
          </Row>
        )}
      </RSModal>
    );
  };
}

Modal.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func.isRequired,
  size: PropTypes.oneOf(["sm", "md", "lg", "xl", "xxl"]),
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
  docUrl: PropTypes.string,
  closeOnEscape: PropTypes.bool,
  body: PropTypes.func.isRequired,
  footer: PropTypes.func,
  onOpened: PropTypes.func,
  onClosed: PropTypes.func,
  className: PropTypes.string,
  bodyClassName: PropTypes.string,
  dataTestId: PropTypes.string,
  docUrlTooltipText: PropTypes.string,
  stickToHeader: PropTypes.bool,
  backdrop: PropTypes.string,
  titleAction: PropTypes.func,
  autoFocus: PropTypes.bool
};

Modal.defaultProps = {
  isOpen: false,
  autoFocus: true,
  size: "md",
  closeOnEscape: true,
  backdrop: "static",
  bodyClassName: null,
  docUrl: null,
  dataTestId: "orion-modal"
};

Modal.displayName = "OrionModal";

Modal.Header = function MHeader(props) {
  const { header } = props;
  return <h5 className={gatherClasses(props, "oui-title")}>{header}</h5>;
};

Modal.Header.propTypes = {
  header: PropTypes.string
};

Modal.Header.displayName = "OrionModalHeader";

Modal.Header.propTypes = {
  header: PropTypes.string
};

Modal.Header.defaultProps = {
  header: ""
};

export default Modal;
